import React, { useMemo } from 'react';
import type { ReactNode } from 'react';
import { Button, Select, Bubble } from 'tea-component';
import { useIntl } from 'umi';
import SvgIcon from '@home/components/SvgIcon';
import TipContext from '@home/components/TipContextComponents/TipContext';
import { LANGUAGE_EN } from '@home/_config/config';

interface IFilterOptionItem {
  i: string;
  filterName: string;
}

interface IExistingFilterOption {
  i: string;
  filterType: string;
  dataset: string;
  filterName: string;
}

interface ISelectOption {
  groupKey: string;
  value: string;
  text: ReactNode;
  disabled?: boolean;
}

interface IRelevantFilterConfigProps {
  relevantFilters: IFilterOptionItem[];
  dependFilters: IFilterOptionItem[];
  filters: IExistingFilterOption[];
  dataset: string;
  onChange: (value: IFilterOptionItem | null, changeIndex?: number) => void;
  currentFilterKey: string;
}

const RelevantFilterConfig = (props: IRelevantFilterConfigProps) => {
  // relentFilters 是 已经被选中的子筛选器
  // dependFilters 是 当前筛选器 依赖了哪些父筛选器
  // filterOptions 是 所有已存在的筛选器
  // dataset 是被选中的数据集
  const {relevantFilters, dependFilters,  filters, dataset, onChange, currentFilterKey} = props;
  const {formatMessage, locale} = useIntl();
  const isEn = locale === LANGUAGE_EN;
  const groups = {
    'same': formatMessage({id: 'dbl_create_dashboard_same_dataset'}),
  };
  const allOptions = useMemo(() => {
    // 可以成为子筛选器选项的条件
    // 1. 必须是字符类型
    // 2. 必须是同一个数据集
    // 3. 已经被选为子筛选器的, 需要选项置灰, 并弹出对应提示
    // 4. 父子筛选器不可以互相选择, 选项需要置灰, 并弹出对应提示
    const options: ISelectOption[] = [];
    filters?.forEach(item => {
      
      if (item.filterType === 'string' && item.dataset === dataset && (item.i !== currentFilterKey)) {
        
        let disabled = false;
        let tooltipId = '';
        if (relevantFilters.some(filter => filter.i === item.i)) {
          disabled = true;
          tooltipId = 'dbl_create_dashboard_relevant_has_add_info';
        } else if (dependFilters.some(filter => filter.i === item.i)) {
          disabled = true;
          tooltipId = 'dbl_create_dashboard_relevant_has_parent';
        }
        
        options.push({
          groupKey: 'same',
          value: item.i,
          text: tooltipId ? (
            <Bubble
              style={{ maxWidth: 280 }}
              content={formatMessage(
                { id: tooltipId },
                { filterName: item.filterName }
              )}
              dark
            >
              <div>{item.filterName}</div>
            </Bubble>
          ) : (
            item.filterName
          ),
          disabled,
        });
      }
    });
    
    return options;
  }, [relevantFilters, dependFilters, filters, dataset, locale]);
  
  return (<div className={'dbl-exit-filter-relevant'}>
    <div className={'dbl-exit-item-title'}>
      {formatMessage({id: 'dbl_create_dashboard_relevant_config_title'})}
      <TipContext
        style={{maxWidth: 400}}
        tipText={formatMessage({id: 'dbl_create_dashboard_relevant_config_info'})}
        placement={isEn ? "top-end" : "top"}
        placementOffset={isEn ? [18, 10] : undefined}
      />
    </div>
    <Select
      className={'dbl-exit-filter-relevant-add-select'}
      appearance="pure"
      groups={groups}
      options={allOptions}
      onChange={(value, context) => {
        
        onChange({ i: value, filterName: String(context?.option?.text || '') });
      }}
      value={null}
      button={(<Button type={'link'}><span>+</span> <div>{formatMessage({id: 'dbl_create_dashboard_relevant_config_add'})}</div></Button>)}
      listWidth={210}
      listHeight={300}
      overlayClassName={'dbl-exit-filter-relevant-overlay'}
    />
  
    {
      relevantFilters?.map((filter, index) => {
        const options = allOptions.map(opt => {
          if (opt.value === filter.i) return {groupKey: 'same', value: filter.i, text: filter.filterName, };
          return opt;
        });
        return (<div key={filter.i} className={'dbl-exit-filter-relevant-child-select'}>
          <Select
            listHeight={300}
            overlayClassName={'dbl-exit-filter-relevant-overlay'}
            matchButtonWidth
            appearance="button"
            groups={groups}
            options={options}
            defaultValue={filter.i}
            onChange={(value, context) => {
      
              onChange(
                { i: value, filterName: context?.option?.text as string },
                index
              );
            }}
          />
          <Bubble
            content={formatMessage({id: 'dbl_common_delete'})}
            dark
          >
            <span className={'delete-icon'} onClick={() => {
  
              onChange(null, index);
            }}>
              <SvgIcon type={'trash-can-icon'} />
            </span>
  
          </Bubble>


        </div>);
      })
    }
    
    
    
  </div>);
  
};

export default RelevantFilterConfig;